// Global Components
import React, { useState, forwardRef, useEffect } from 'react';
import * as Yup from 'yup';
import { httpsCallable } from 'firebase/functions';

import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  setPersistence,
  updateProfile,
} from 'firebase/auth';
import firebase, { analytics, functions } from '@breakroom-nx/apis';
import { logEvent } from 'firebase/analytics';
import { Box, VStack, Text, Heading, Button } from 'native-base';
import { Formik, ErrorMessage } from 'formik';

// Platform Specific
import { DatePickerModal } from 'react-native-paper-dates';

// Assets
import {
  ArrowRightIcon,
  EyeOffIcon as AnonIcon,
  CakeIcon,
} from 'react-native-heroicons/solid';

// Shared Libraries
import { auth } from '@breakroom-nx/apis';
import { theme } from '@breakroom-nx/assets';

// Components
import {
  TextField,
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from '@breakroom-nx/ui';
import { InputType, Status, AppData } from '@breakroom-nx/helpers';
import {
  useAppDispatch,
  openModal,
  flipToPane,
  useGetAppMetadataQuery,
} from '@breakroom-nx/redux';
import {
  Shuffler,
  User,
  shuffle,
  shuffleColor,
  getAvatarURL,
} from '@breakroom-nx/classes';

const usernameRegExp = /\w/;
const nameRegExp = /^[A-Za-z\s-']+$/;
const RegistrationSchema = Yup.object().shape({
  username: Yup.string()
    .matches(
      usernameRegExp,
      "Only letters, numbers and '_' underscores allowed"
    )
    .max(32, 'Too Long!')
    .required('Choose a username'),
  name: Yup.string()
    .matches(
      nameRegExp,
      'Only letters, spaces, hyphens and apostrophes allowed'
    )
    .max(50, 'Too Long!')
    .optional(),
  email: Yup.string().email('Invalid email').required('Enter your email'),
  password: Yup.string().min(6, 'Too Short!').required('Choose a password'),
});

// /* eslint-disable-next-line */
export interface SignupProps {
  onSuccess: () => void;
  isAnonymous: boolean;
  username?: string;
}

interface SignupFormValues {
  username: string;
  name?: string;
  email: string;
  password: string;
  // alphaCode: string;
}

export function Signup({
  isAnonymous = false,
  username = undefined,
  onSuccess,
}: SignupProps) {
  const [date, setDate] = React.useState(undefined);
  const [open, setOpen] = React.useState(false);
  const dispatch = useAppDispatch();
  const { data: metadata } = useGetAppMetadataQuery(null);
  const initialValues: SignupFormValues = {
    username: '',
    name: '',
    email: '',
    password: '',
    // alphaCode: '',
  };

  // States
  const [dateOfBirth, setDateOfBirth] = useState<Date>(new Date());
  const [dateOfBirthValid, setDateOfBirthValid] = useState<boolean>(false);
  const createUser = httpsCallable(functions, 'user-createuser');

  function calculateAge(date: Date) {
    const dateNow = Date.now();
    const datePassed = date.getTime();
    const ageDifMs = dateNow - datePassed;
    const ageDate = new Date(ageDifMs);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  }

  function validateAge(date: Date) {
    setDateOfBirth(date);
    const age: number = calculateAge(date);
    // console.log(`New Age: ${age}`);

    if (date && age >= AppData.AGE_REQUIREMENT) {
      setDateOfBirthValid(true);
    } else {
      setDateOfBirthValid(false);
    }
  }

  const onDismissSingle = React.useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const onConfirmSingle = React.useCallback(
    (params) => {
      setOpen(false);
      setDate(params.date);
      validateAge(params.date);
    },
    [setOpen, setDate]
  );

  useEffect(() => {
    // console.log(`Rendered Signup Page`);
  });

  return (
    <VStack space={7}>
      <Heading mt={7} fontSize={30} lineHeight={34}>
        <Text color={'primary.400'}>
          {`${
            (metadata?.registeredUsersCount ?? 0) +
            (metadata?.unregisteredUsersCount ?? 0)
          } online `}
        </Text>
        in <Text color={'red.200'}>{metadata?.roomCount ?? 0} rooms</Text>,
        start chatting!
      </Heading>

      {!isAnonymous ? (
        <Box width={'full'}>
          <TertiaryButton
            onPress={() => {
              dispatch(flipToPane('Login'));
            }}
            bg={'transparent'}
            color={'white'}
            text={'Already have an account? Login'}
            endIcon={
              <ArrowRightIcon color={theme.colors.primary[400]} size={15} />
            }
          />
        </Box>
      ) : null}

      {/* FORM */}
      <Formik
        initialValues={initialValues}
        validationSchema={RegistrationSchema}
        onSubmit={(values, { setSubmitting }) => {
          // Final Check Against Age
          if (!dateOfBirthValid) {
            setSubmitting(false);
            dispatch(
              openModal({
                modalType: 'Alert',
                modalProps: {
                  status: Status.error,
                  title: 'You are not old enough to use this app.',
                  description:
                    'If that was a mistake, please press the age button and update your date of birth.',
                },
              })
            );
          } else {
            // dispatch(
            //   openModal({
            //     modalType: 'Alert',
            //     modalProps: {
            //       status: Status.error,
            //       title: 'Please provide a valid invite code.',
            //       description:
            //         'Please check your invite email to make sure you entered the code correctly',
            //     },
            //   })

            // Check that the alpha invite code is valid
            // const isInviteCodeValid = httpsCallable(
            //   functions,
            //   'user-validinvitecode'
            // );

            const isUsernameAvailable = httpsCallable(
              functions,
              'user-usernameavailable'
            );

            // isInviteCodeValid({ text: values.alphaCode })
            //   .then((codeValidResult) => {
            isUsernameAvailable({ text: values.username }).then((result) => {
              const available = result.data;
              if (available) {
                createUserWithEmailAndPassword(
                  auth,
                  values.email,
                  values.password
                )
                  .then(async (credential) => {
                    await updateProfile(credential.user, {
                      displayName: values.username,
                    });

                    const shuffler: Shuffler = shuffle();

                    // Get temporary avatar
                    const avatarURL = await getAvatarURL(
                      `avatars/${shuffler.object.noun}.png`
                    )
                      .then((url) => {
                        // console.log(`[SIGNUP] Got avatar URL: ${url}`);
                        return url;
                      })
                      .catch((err) => {
                        // console.log(
                        //   `[SIGNUP] Failed to Get Avatar: [${err.code} | ${err.message}]`
                        // );
                        return undefined;
                      });

                    await createUser({
                      username: values.username,
                      dateOfBirth: dateOfBirth,
                      theme: shuffler.color,
                      avatar: avatarURL,
                      name: values.name,
                    })
                      .then((result) => {
                        // console.log(`Done Uploading User Data`);
                      })
                      .catch((err) => {
                        // console.log(`User Data Upload Failed`);
                      });

                    // console.log(
                    //   `SUCCESS: - Account created and signed in: ${credential.user.uid}`
                    // );
                    // logEvent(analytics, 'sign_up');
                    setSubmitting(false);
                    sendEmailVerification(credential.user)
                      .then((sent) => {
                        setPersistence(auth, browserLocalPersistence)
                          .then(() => {
                            onSuccess();
                          })
                          .catch((error) => {
                            const errorCode = error.code;
                            const errorMessage = error.message;
                            // console.log(
                            //   `[Local Persistence] (Failed) ${errorCode}: ${errorMessage}`
                            // );
                            onSuccess();
                          });
                      })
                      .catch((error) => {
                        // console.log(
                        //   `[SIGNUP] (Failed) ${error.errorCode}: ${error.errorMessage}`
                        // );
                        onSuccess();
                      });
                  })
                  .catch((error) => {
                    setSubmitting(false);
                    const errorCode = error.code;
                    let errorString = undefined;
                    if (errorCode === 'auth/email-already-in-use') {
                      errorString = 'Account already exists for this email.';
                    } else {
                      errorString = `${errorCode}: ${error.errorMessage}`;
                    }
                    // console.log(
                    //   `ISSUE FOUND: ${errorCode}: ${error.errorMessage}`
                    // );

                    dispatch(
                      openModal({
                        modalType: 'Alert',
                        modalProps: {
                          status: Status.error,
                          title: errorString,
                        },
                      })
                    );
                  });
              } else {
                setSubmitting(false);
                dispatch(
                  openModal({
                    modalType: 'Alert',
                    modalProps: {
                      status: Status.warning,
                      title: 'Username Unavailable',
                    },
                  })
                );
              }
            });
            // .catch((err) => {
            //   console.log(`Error: ${err}`);
            // });

            //   })
            //   .catch((err) => {
            //     console.log(`Error: ${err}`);
            //   });
          }
        }}
      >
        {({ values, isSubmitting, handleChange, handleBlur, handleSubmit }) => (
          <VStack space={'3'} width={'full'}>
            <TextField
              disabled={isSubmitting}
              type={InputType.Username}
              onChangeText={handleChange('username')}
              onBlur={handleBlur('username')}
              placeholder={username ?? undefined}
              value={values.username}
              autoFocus
            />
            <Text variant={'error'}>
              <ErrorMessage name="username" />
            </Text>
            <TextField
              disabled={isSubmitting}
              type={InputType.Name}
              onChangeText={handleChange('name')}
              onBlur={handleBlur('name')}
              value={values.name}
            />
            <Text variant={'error'}>
              <ErrorMessage name="name" />
            </Text>
            <TextField
              disabled={isSubmitting}
              type={InputType.Email}
              onChangeText={handleChange('email')}
              onBlur={handleBlur('email')}
              value={values.email}
              autoCompleteType={'email'}
            />
            <Text variant={'error'}>
              <ErrorMessage name="email" />
            </Text>
            <TextField
              disabled={isSubmitting}
              type={InputType.Password}
              onChangeText={handleChange('password')}
              onBlur={handleBlur('password')}
              value={values.password}
              autoCompleteType={'password'}
            />
            <Text variant={'error'}>
              <ErrorMessage name="password" />
            </Text>
            <Button
              onPress={() => setOpen(true)}
              _pressed={{ backgroundColor: 'primary.300' }}
              startIcon={
                <CakeIcon
                  color={
                    dateOfBirthValid
                      ? theme.colors.primary[400]
                      : theme.colors.red[200]
                  }
                  size={15}
                />
              }
              px={5}
              py={4}
              size={'md'}
              width={'full'}
              bg={'surface.300'}
              opacity={0.75}
              rounded={'full'}
              _hover={{
                opacity: 1,
                // backgroundColor: bg,
              }}
              _text={{
                textAlign: 'center',
                color: `${dateOfBirthValid ? 'primary.400' : 'red.200'}`,
                fontSize: 16,
                fontFamily: 'body',
                fontWeight: 600,
                fontStyle: 'normal',
              }}
            >{`${calculateAge(dateOfBirth)} Years Old`}</Button>

            <DatePickerModal
              locale="en"
              mode="single"
              visible={open}
              onDismiss={onDismissSingle}
              date={date}
              onConfirm={onConfirmSingle}
            />

            <Text variant={'error'} textAlign={'center'}>
              {!dateOfBirthValid ? (
                <Text color={'surface.600'} fontSize={15}>
                  You must be at least{' '}
                  <Text color={'red.200'} fontWeight={600}>
                    {AppData.AGE_REQUIREMENT} years old
                  </Text>{' '}
                  to use the app.
                </Text>
              ) : (
                <Text color={'primary.400'} fontSize={15}>
                  You're old enough!
                </Text>
              )}
            </Text>
            {/* <TextField
              disabled={isSubmitting}
              type={InputType.AlphaCode}
              onChangeText={handleChange('alphaCode')}
              onBlur={handleBlur('alphaCode')}
              value={values.alphaCode}
            />
            <Text variant={'error'}>
              <ErrorMessage name="alphaCode" />
            </Text> */}

            <Text
              color={'surface.600'}
              textAlign={'center'}
              fontSize={15}
              mt={5}
            >
              By creating an account, you agree to the{' '}
              <a
                href={
                  'https://app.termly.io/policy-viewer/policy.html?policyUUID=71c165d8-a9d6-4991-9af6-371f16ba897a'
                }
              >
                <Text color={'primary.400'}>Terms and Conditions</Text>
              </a>{' '}
              and the{' '}
              <a
                href={
                  'https://app.termly.io/policy-viewer/policy.html?policyUUID=000f40f7-6bbe-4456-9758-32d4bde72439'
                }
              >
                <Text color={'primary.400'}>Privacy Policy</Text>
              </a>
            </Text>
            <VStack mt={5} space={4} width={'full'} alignItems={'center'}>
              <PrimaryButton
                disabled={isSubmitting}
                isLoading={isSubmitting}
                text={'Create account'}
                onPress={handleSubmit}
                width={'100%'}
              />
              {!isAnonymous ? (
                <VStack space={4} width={'full'} alignItems={'center'}>
                  <Text fontSize={15} fontWeight={500}>
                    or
                  </Text>
                  <SecondaryButton
                    disabled={isSubmitting}
                    color={'primary.400'}
                    text={'Browse anonymously'}
                    width={'100%'}
                    onPress={() => {
                      dispatch(flipToPane('Anonymous'));
                    }}
                    startIcon={
                      <AnonIcon color={theme.colors.primary[400]} size={15} />
                    }
                  />
                </VStack>
              ) : null}
            </VStack>
          </VStack>
        )}
      </Formik>
    </VStack>
  );
}

export default React.memo(Signup);
